import i18n from '@/i18n';
import { infringementAPI } from '@/utils/api';
import { convertImageToServerUrl } from '@/utils/imageUrlUtils';
import { getProductMeta, setProductMeta } from './platformListingProductMeta';
import { toProcessableImageUrl } from './sourcingWorkflowSpace';
import { pollInfringementBatch } from './infringementBatchPoll';

/** 侵权检测 riskLevel → 商品上架 meta.risk */
export function mapInfringementRiskToListing(riskLevel) {
  const r = String(riskLevel || 'unknown').toLowerCase();
  if (r === 'high') return 'high';
  if (r === 'medium') return 'medium';
  if (r === 'low') return 'low';
  return 'pending';
}

function primaryImageForRisk(row) {
  if (!row || typeof row !== 'object') return '';
  const primary = row.primaryImageUrl || row.remotePrimaryImageUrl || '';
  if (primary) return primary;
  const urls = Array.isArray(row.imageUrls) ? row.imageUrls : [];
  return urls.find((u) => u && typeof u === 'string') || '';
}

async function resolveServerImageUrl(imageUrl) {
  const full = String(imageUrl || '').trim();
  if (!full) throw new Error(i18n.t('workbench:ipeUtilError.imageUrlUnresolved'));
  if (full.startsWith('/uploads/')) {
    return full;
  }
  if (full.startsWith('http') && !full.startsWith('data:')) {
    return full;
  }
  const data = full.startsWith('data:') ? full : await toProcessableImageUrl(full);
  if (!data) throw new Error(i18n.t('workbench:ipeUtilError.imageReadFailed'));
  const server = await convertImageToServerUrl(data);
  if (!server) throw new Error(i18n.t('workbench:ipeUtilError.imageUploadServerFailed'));
  return server;
}

/**
 * 对单条待上品执行深度侵权检测并写入本地 meta（供商品上架列表展示）。
 * createBatch 异步返回后轮询 getBatch 直至完成。
 * @returns {Promise<string>} meta.risk
 */
export async function assessPlatformListingRiskForRow(row, { imageUrl: overrideUrl, onMetaChange } = {}) {
  const id = row?.id;
  if (id == null) return 'pending';

  setProductMeta(id, { assessing: true });
  onMetaChange?.();

  const imageUrl = String(overrideUrl || '').trim() || primaryImageForRisk(row);
  if (!imageUrl) {
    setProductMeta(id, {
      risk: 'pending',
      assessing: false,
      riskAdvice: '缺少主图，暂无法评估',
      assessedAt: new Date().toISOString(),
    });
    onMetaChange?.();
    return 'pending';
  }

  try {
    const serverUrl = await resolveServerImageUrl(imageUrl);
    const created = await infringementAPI.createBatch({
      imageUrls: [serverUrl],
      mode: 'deep',
      remark: `商品库侵权检测 · 条目 ${id}`,
    });
    const batchId = created?.id;
    if (!batchId) throw new Error(i18n.t('workbench:ipeUtilError.infringementCreateFailed'));

    const detail = await pollInfringementBatch(batchId, {
      timeoutMessage: i18n.t('workbench:ipeUtilError.riskAssessTimeout'),
    });
    const items = Array.isArray(detail?.items) ? detail.items : [];
    const top = items[0];
    const risk = mapInfringementRiskToListing(top?.riskLevel);
    const advice = top?.advice || '';

    setProductMeta(id, {
      risk,
      assessing: false,
      riskAdvice: advice,
      infringementBatchId: batchId,
      assessedAt: new Date().toISOString(),
    });
    onMetaChange?.();
    return risk;
  } catch (e) {
    setProductMeta(id, {
      risk: 'pending',
      assessing: false,
      riskAdvice: e?.message || '侵权检测失败',
      assessedAt: new Date().toISOString(),
    });
    onMetaChange?.();
    return 'pending';
  }
}

/** 批量评估（限制并发，避免同时创建过多检测批次） */
export async function assessPlatformListingRisksForRows(
  rows,
  { concurrency = 2, imageUrlByItemId = {}, onMetaChange } = {},
) {
  const list = Array.isArray(rows) ? rows.filter((r) => r?.id != null) : [];
  if (list.length === 0) return;

  let index = 0;
  async function worker() {
    while (index < list.length) {
      const i = index;
      index += 1;
      const row = list[i];
      const overrideUrl = imageUrlByItemId[row.id] ?? imageUrlByItemId[String(row.id)];
      await assessPlatformListingRiskForRow(row, { imageUrl: overrideUrl, onMetaChange });
    }
  }
  const workers = Math.min(concurrency, list.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
}

/** 送入商品库后可选触发评估（默认不自动检测，由商品库手动批量/单条检测） */
export function schedulePlatformListingRiskAssessment(rows) {
  const need = (Array.isArray(rows) ? rows : []).filter((row) => {
    if (row?.id == null) return false;
    const meta = getProductMeta(row.id);
    return meta.risk === 'pending' && !meta.assessing;
  });
  if (need.length === 0) return;
  void assessPlatformListingRisksForRows(need);
}
